import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  dealDetailSchema,
  generateDealResultSchema,
  projectSchema,
  type DealDetail,
  type GenerateDealResult,
  type Project,
} from '@forecastify/shared';
import { Button, Dialog } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { useWorkspaceNavigate } from '../../workspace-path';
import { parseList } from '../permissions-hook';
import { dealRefusal, type DealFeeds } from './deal-kit';
import {
  hasProject,
  initialDraft,
  invalidStep,
  nextStep,
  previewOf,
  previousStep,
  toRequest,
  type GenerateDraft,
  type Problem,
} from './generate-plan';
import {
  ContractStep,
  ConversionList,
  DoneView,
  ExistingProjectStep,
  ProjectStep,
  ResourcesStep,
  RouteStep,
  StepIndicator,
} from './generate-steps';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D8 · the *Generálás* wizard — `14-deal-generate-*`. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Loads the deal itself (`GET /deals/:ref`), walks four steps — what, project, contract and line
 * items, resources — and posts them as one `POST /deals/:id/generate`.
 *
 * ⚠️⚠️ **GENERATION IS NOT ONE TRANSACTION** (`decisions.md` DM-8). The server makes the project,
 * then the contract, then line items, tasks and bookings, each through its own service, and a
 * refusal halfway leaves what was made — linked to the deal. So a refusal here shows the sentence
 * AND reloads the deal, and lists what exists now, rather than implying nothing happened.
 */
export interface GenerateWizardProps {
  readonly open: boolean;
  /** The deal's uuid or identifier. */
  readonly dealRef: string;
  readonly feeds: DealFeeds;
  readonly onClose: () => void;
  readonly onDone: (result: GenerateDealResult) => void;
}

type Conversion = DealDetail['conversions'][number];

export function GenerateWizard({ open, dealRef, feeds, onClose, onDone }: GenerateWizardProps) {
  const t = useT();
  const session = useSession();
  const go = useWorkspaceNavigate();

  const [deal, setDeal] = useState<DealDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<GenerateDraft | null>(null);
  const [step, setStep] = useState(0);
  /** The client's projects, for the `contract` route; null until asked for and answered. */
  const [projects, setProjects] = useState<readonly Project[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** After a refusal: what the attempt made before it stopped. */
  const [partial, setPartial] = useState<readonly Conversion[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GenerateDealResult | null>(null);

  const fetchDeal = useCallback(async (): Promise<DealDetail | string> => {
    const response = await session
      .request(`/deals/${encodeURIComponent(dealRef)}`)
      .catch(() => null);
    if (response === null) return t('error.unavailable');
    if (!response.ok) return dealRefusal(response, t);
    const parsed = dealDetailSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : t('error.unavailable');
  }, [session, dealRef, t]);

  /* A fresh wizard every time it opens. */
  useEffect(() => {
    if (!open) return;
    setDeal(null);
    setDraft(null);
    setLoadError(null);
    setStep(0);
    setProjects(null);
    setError(null);
    setPartial(null);
    setResult(null);
    void (async () => {
      const loaded = await fetchDeal();
      if (typeof loaded === 'string') {
        setLoadError(loaded);
        return;
      }
      setDeal(loaded);
      setDraft(initialDraft(loaded));
    })();
  }, [open, fetchDeal]);

  /* The existing-project picker's list, fetched the first time the `contract` route asks. */
  const clientId = deal?.clientId ?? null;
  const wantsProjects = draft?.route === 'contract' && draft.target === 'project';
  useEffect(() => {
    if (!wantsProjects || projects !== null || clientId === null) return;
    void (async () => {
      const response = await requestAll(session, '/projects').catch(() => null);
      const parsed = response?.ok === true ? parseList(projectSchema, await response.json()) : null;
      setProjects((parsed ?? []).filter((project) => project.clientId === clientId));
    })();
  }, [wantsProjects, projects, clientId, session]);

  const preview = useMemo(
    () => (deal === null || draft === null ? null : previewOf(deal, draft)),
    [deal, draft],
  );

  const patch = useCallback((change: Partial<GenerateDraft>): void => {
    setDraft((current) => (current === null ? current : { ...current, ...change }));
    setError(null);
  }, []);

  const problemText = (which: Problem): string => {
    if (which === 'existing') return t('dealGenerate.error.existing');
    if (which === 'contract') return t('dealGenerate.error.contract');
    return t('dealGenerate.error.project');
  };

  const next = (): void => {
    if (draft === null) return;
    const problem = invalidStep(draft);
    if (problem !== null && problem.step === step) {
      setError(problemText(problem.which));
      return;
    }
    setError(null);
    setStep(nextStep(step, draft));
  };

  const back = (): void => {
    if (draft === null) return;
    setError(null);
    setStep(previousStep(step, draft));
  };

  const submit = async (): Promise<void> => {
    if (deal === null || draft === null) return;
    const problem = invalidStep(draft);
    if (problem !== null) {
      setStep(problem.step);
      setError(problemText(problem.which));
      return;
    }
    setBusy(true);
    setError(null);
    setPartial(null);
    const response = await session
      .request(`/deals/${encodeURIComponent(deal.id)}/generate`, {
        method: 'POST',
        body: JSON.stringify(toRequest(deal, draft)),
      })
      .catch(() => null);
    if (response?.ok === true) {
      const parsed = generateDealResultSchema.safeParse(await response.json());
      setBusy(false);
      if (parsed.success) {
        setResult(parsed.data);
        return;
      }
      setError(t('error.unavailable'));
      return;
    }
    setError(response === null ? t('error.unavailable') : await dealRefusal(response, t));
    /* ⚠️ Not one transaction — read back what the attempt left behind. */
    const before = deal.conversions.length;
    const reloaded = await fetchDeal();
    setBusy(false);
    if (typeof reloaded === 'string') return;
    setDeal(reloaded);
    setPartial(reloaded.conversions.slice(before));
  };

  const finish = (): void => {
    if (result === null) onClose();
    else onDone(result);
  };

  const openProject = (): void => {
    const id = result?.projectId ?? null;
    if (id === null) return;
    finish();
    go(`/projects/${id}`);
  };
  const openContract = (): void => {
    const identifier = result?.contractIdentifier ?? null;
    if (identifier === null) return;
    finish();
    go(`/contracts/${identifier}`);
  };

  const dealLabel = deal?.identifier ?? '';
  const title =
    result !== null
      ? t('dealGenerate.done.title', { deal: dealLabel })
      : deal !== null && deal.conversions.length > 0
        ? t('dealGenerate.titleAgain', { deal: dealLabel })
        : t('dealGenerate.title', { deal: dealLabel });

  const labels = [
    t('dealGenerate.step.route'),
    t('dealGenerate.step.project'),
    t('dealGenerate.step.contract'),
    t('dealGenerate.step.resources'),
  ];

  let body: ReactNode = null;
  if (deal !== null && draft !== null && preview !== null) {
    const withProject = hasProject(draft);
    if (step === 0) body = <RouteStep deal={deal} draft={draft} patch={patch} />;
    else if (step === 1 && draft.route === 'contract')
      body = <ExistingProjectStep draft={draft} patch={patch} projects={projects} />;
    else if (step === 1)
      body = (
        <ProjectStep
          deal={deal}
          draft={draft}
          patch={patch}
          members={feeds.members}
          preview={preview}
        />
      );
    else if (step === 2)
      body = (
        <ContractStep
          deal={deal}
          draft={draft}
          patch={patch}
          withProject={withProject}
          preview={preview}
        />
      );
    else
      body = (
        <ResourcesStep
          deal={deal}
          draft={draft}
          patch={patch}
          members={feeds.members}
          preview={preview}
          projects={projects}
        />
      );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) finish();
      }}
      title={title}
      description={result === null ? t('dealGenerate.description') : undefined}
      closeLabel={t('common.close')}
      width={840}
    >
      {result !== null ? (
        <div className="flex flex-col gap-4">
          <DoneView result={result} onOpenProject={openProject} onOpenContract={openContract} />
          <div className="flex items-center justify-end gap-2">
            <Button size="lg" onClick={finish}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      ) : loadError !== null ? (
        <div className="flex flex-col gap-4">
          <div className="text-destructive text-[12px]" role="alert">
            {loadError}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="lg" onClick={onClose}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      ) : body === null ? (
        <div className="text-muted-foreground py-6 text-center text-[12px]" role="status">
          {t('common.loading')}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <StepIndicator step={step} labels={labels} />
          {body}

          {error === null ? null : (
            <div className="text-destructive text-[12px]" role="alert">
              {error}
            </div>
          )}
          {partial === null ? null : (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-[11.5px] leading-relaxed">
              {partial.length === 0 ? (
                <span>{t('dealGenerate.partial.none')}</span>
              ) : (
                <>
                  <span className="font-medium">{t('dealGenerate.partial.title')}</span>
                  <ConversionList conversions={partial} />
                  <span className="text-muted-foreground">{t('dealGenerate.partial.hint')}</span>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            {step > 0 ? (
              <Button variant="ghost" size="lg" onClick={back}>
                {t('common.back')}
              </Button>
            ) : null}
            <div className="flex-1" />
            <Button variant="outline" size="lg" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            {step === 3 ? (
              /* ⚠️ DM-8 · after a refusal that already made something, a second press would make a
                 second project. Generating again starts from a fresh wizard, whose first step
                 lists what exists. */
              <Button
                size="lg"
                disabled={busy || (partial !== null && partial.length > 0)}
                onClick={() => void submit()}
              >
                {busy ? t('dealGenerate.running') : t('dealGenerate.submit')}
              </Button>
            ) : (
              <Button size="lg" onClick={next}>
                {t('dealGenerate.next')}
              </Button>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
